const numberHelper = {
    methods: {
        // Formats a number with thousand separators
        addCommas(num, decimalPlaces = null) {
            if (num === null || num === undefined || isNaN(num)) return '0';

            const number = parseFloat(num);
            let formatted = decimalPlaces !== null
                ? number.toFixed(decimalPlaces)
                : number.toString();

            const parts = formatted.split('.');
            parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

            return parts.join('.');
        },

        // Formats a number as currency
        formatCurrency(num, currency = '$', decimalPlaces = 2, position = 'before') {
            if (num === null || num === undefined || isNaN(num)) return `${currency}0.00`;

            const formatted = this.addCommas(num, decimalPlaces);
            return position === 'before' ? `${currency}${formatted}` : `${formatted}${currency}`;
        },

        // Formats a number as percentage
        formatPercentage(num, decimalPlaces = 2, includeSymbol = true) {
            if (num === null || num === undefined || isNaN(num)) return '0%';

            const number = parseFloat(num);
            const formatted = number.toFixed(decimalPlaces);

            return includeSymbol ? `${formatted}%` : formatted;
        },

        // Converts a number to percentage (e.g., 0.5 to 50%)
        toPercentage(num, decimalPlaces = 2, includeSymbol = true) {
            if (num === null || num === undefined || isNaN(num)) return '0%';

            const percentage = parseFloat(num) * 100;
            const formatted = percentage.toFixed(decimalPlaces);

            return includeSymbol ? `${formatted}%` : formatted;
        },

        // Formats large numbers with abbreviations (K, M, B, T)
        formatAbbreviated(num, decimalPlaces = 1) {
            if (num === null || num === undefined || isNaN(num)) return '0';

            const number = parseFloat(num);
            const absNum = Math.abs(number);
            const sign = number < 0 ? '-' : '';

            if (absNum >= 1e12) {
                return sign + (absNum / 1e12).toFixed(decimalPlaces) + 'T';
            } else if (absNum >= 1e9) {
                return sign + (absNum / 1e9).toFixed(decimalPlaces) + 'B';
            } else if (absNum >= 1e6) {
                return sign + (absNum / 1e6).toFixed(decimalPlaces) + 'M';
            } else if (absNum >= 1e3) {
                return sign + (absNum / 1e3).toFixed(decimalPlaces) + 'K';
            }

            return sign + absNum.toFixed(decimalPlaces);
        },

        // Rounds a number to a specific number of decimal places
        roundTo(num, decimalPlaces = 2) {
            if (num === null || num === undefined || isNaN(num)) return 0;

            const multiplier = Math.pow(10, decimalPlaces);
            return Math.round(parseFloat(num) * multiplier) / multiplier;
        },

        // Rounds up a number to a specific number of decimal places
        ceilTo(num, decimalPlaces = 2) {
            if (num === null || num === undefined || isNaN(num)) return 0;

            const multiplier = Math.pow(10, decimalPlaces);
            return Math.ceil(parseFloat(num) * multiplier) / multiplier;
        },

        // Rounds down a number to a specific number of decimal places
        floorTo(num, decimalPlaces = 2) {
            if (num === null || num === undefined || isNaN(num)) return 0;

            const multiplier = Math.pow(10, decimalPlaces);
            return Math.floor(parseFloat(num) * multiplier) / multiplier;
        },

        // Clamps a number between a min and max value
        clamp(num, min, max) {
            if (num === null || num === undefined || isNaN(num)) return min;

            return Math.min(Math.max(parseFloat(num), min), max);
        },

        // Checks if a value is a valid number
        isNumber(value) {
            return typeof value === 'number' && !isNaN(value) && isFinite(value);
        },

        // Checks if a number is even
        isEven(num) {
            if (num === null || num === undefined || isNaN(num)) return false;
            return parseInt(num) % 2 === 0;
        },

        // Checks if a number is odd
        isOdd(num) {
            if (num === null || num === undefined || isNaN(num)) return false;
            return parseInt(num) % 2 !== 0;
        },

        // Checks if a number is positive
        isPositive(num) {
            if (num === null || num === undefined || isNaN(num)) return false;
            return parseFloat(num) > 0;
        },

        // Checks if a number is negative
        isNegative(num) {
            if (num === null || num === undefined || isNaN(num)) return false;
            return parseFloat(num) < 0;
        },

        // Checks if a number is within a range (inclusive)
        isInRange(num, min, max) {
            if (num === null || num === undefined || isNaN(num)) return false;
            const value = parseFloat(num);
            return value >= min && value <= max;
        },

        // Calculates the sum of an array of numbers
        sum(numbers) {
            if (!Array.isArray(numbers)) return 0;
            return numbers.reduce((acc, num) => acc + (parseFloat(num) || 0), 0);
        },

        // Calculates the average of an array of numbers
        average(numbers) {
            if (!Array.isArray(numbers) || numbers.length === 0) return 0;
            return this.sum(numbers) / numbers.length;
        },

        // Finds the minimum value in an array of numbers
        min(numbers) {
            if (!Array.isArray(numbers) || numbers.length === 0) return 0;
            return Math.min(...numbers.map(num => parseFloat(num) || 0));
        },

        // Finds the maximum value in an array of numbers
        max(numbers) {
            if (!Array.isArray(numbers) || numbers.length === 0) return 0;
            return Math.max(...numbers.map(num => parseFloat(num) || 0));
        },

        // Generates a random number between min and max (inclusive)
        random(min = 0, max = 100) {
            return Math.floor(Math.random() * (max - min + 1)) + min;
        },

        // Generates a random decimal number between min and max
        randomDecimal(min = 0, max = 1, decimalPlaces = 2) {
            const random = Math.random() * (max - min) + min;
            return parseFloat(random.toFixed(decimalPlaces));
        },

        // Converts a number to ordinal (1st, 2nd, 3rd, etc.)
        toOrdinal(num) {
            if (num === null || num === undefined || isNaN(num)) return '';

            const n = parseInt(num);
            const s = ['th', 'st', 'nd', 'rd'];
            const v = n % 100;

            return n + (s[(v - 20) % 10] || s[v] || s[0]);
        },

        // Converts a string to a number, with a fallback value
        parseNumber(value, fallback = 0) {
            if (value === null || value === undefined || value === '') return fallback;

            const parsed = parseFloat(value);
            return isNaN(parsed) ? fallback : parsed;
        },

        // Converts a string with commas to a number
        parseFormattedNumber(value, fallback = 0) {
            if (value === null || value === undefined || value === '') return fallback;

            const cleaned = String(value).replace(/,/g, '');
            const parsed = parseFloat(cleaned);
            return isNaN(parsed) ? fallback : parsed;
        },

        // Formats a number with custom thousands and decimal separators
        formatWithSeparators(num, thousandsSep = ',', decimalSep = '.', decimalPlaces = null) {
            if (num === null || num === undefined || isNaN(num)) return '0';

            const number = parseFloat(num);
            let formatted = decimalPlaces !== null
                ? number.toFixed(decimalPlaces)
                : number.toString();

            const parts = formatted.split('.');
            parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);

            return parts.length > 1 ? parts.join(decimalSep) : parts[0];
        },

        // Formats bytes to human-readable file size (KB, MB, GB, etc.)
        formatBytes(bytes, decimalPlaces = 2) {
            if (bytes === null || bytes === undefined || isNaN(bytes) || bytes === 0) return '0 Bytes';

            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));

            return parseFloat((bytes / Math.pow(k, i)).toFixed(decimalPlaces)) + ' ' + sizes[i];
        },

        // Pads a number with leading zeros
        padWithZeros(num, length = 2) {
            if (num === null || num === undefined) return '0'.repeat(length);
            return String(num).padStart(length, '0');
        },

        // Calculates percentage of a value relative to a total
        calculatePercentage(value, total, decimalPlaces = 2) {
            if (total === 0 || total === null || total === undefined) return 0;
            return this.roundTo((parseFloat(value) / parseFloat(total)) * 100, decimalPlaces);
        },

        // Applies a percentage to a number (e.g., 10% of 100 = 10)
        applyPercentage(num, percentage) {
            if (num === null || num === undefined || isNaN(num)) return 0;
            if (percentage === null || percentage === undefined || isNaN(percentage)) return parseFloat(num);

            return parseFloat(num) * (parseFloat(percentage) / 100);
        },

        // Increases a number by a percentage
        increaseByPercentage(num, percentage) {
            if (num === null || num === undefined || isNaN(num)) return 0;
            const increase = this.applyPercentage(num, percentage);
            return parseFloat(num) + increase;
        },

        // Decreases a number by a percentage
        decreaseByPercentage(num, percentage) {
            if (num === null || num === undefined || isNaN(num)) return 0;
            const decrease = this.applyPercentage(num, percentage);
            return parseFloat(num) - decrease;
        }
    }
};

export default numberHelper;
